// Google provider — handles both GA4 (gtag.js) and Google Tag Manager. Both
// scripts install a shared `window.gtag`/`window.dataLayer`, so one
// `gtag('event', ...)` call lands in both subsystems. Merchants may have GA4,
// GTM, or both configured at the store-settings level; this loader is a no-op
// for any ID it has already loaded.

import { AnalyticsEventPayload } from '@/lib/tracking/analytics-events';


declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void;
		dataLayer?: unknown[];
	}
}


const ensureGtagStub = (): void => {
	if(typeof window === 'undefined') { return; }
	window.dataLayer = window.dataLayer || [];
	if(!window.gtag) {
		// gtag.js's standard polyfill pushes the entire arguments tuple onto
		// dataLayer. Using rest params + push(args) preserves that semantic
		// (each entry is the array of args from a single gtag() call).
		window.gtag = function gtag(...args: unknown[]) {
			window.dataLayer!.push(args);
		};
	}
};


export const loadGoogleAnalytics = (trackingId: string | null | undefined): void => {

	if(!trackingId) { return; }
	if(typeof document === 'undefined') { return; }

	const id = `google-analytics-js-${trackingId}`;
	if(document.getElementById(id)) { return; }

	const script = document.createElement('script');
	script.id = id;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
	script.async = true;
	document.head.appendChild(script);

	ensureGtagStub();

	// Let GA4 auto-fire the initial page_view on `config`. Subsequent SPA
	// navigations come from trackPageView. The trackPageView effect skips its
	// first invocation so we don't double-count the landing page.
	window.gtag!('js', new Date());
	window.gtag!('config', trackingId);
};


export const loadGoogleTagManager = (gtmId: string | null | undefined): void => {

	if(!gtmId) { return; }
	if(typeof document === 'undefined') { return; }

	const id = `google-tag-manager-${gtmId}`;
	if(document.getElementById(id)) { return; }

	const firstScript = document.getElementsByTagName('script')[0];
	const script = document.createElement('script');
	script.id = id;
	script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
	script.async = true;
	if(firstScript && firstScript.parentNode) {
		firstScript.parentNode.insertBefore(script, firstScript);
	} else {
		document.head.appendChild(script);
	}

	ensureGtagStub();
	// GTM's start event is a dataLayer message object, so push it directly.
	// Routing it through the gtag stub would wrap it in an array (gtag pushes its
	// arguments tuple), which is not the shape GTM's container expects.
	window.dataLayer!.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
};


export const dispatchGoogle = (eventName: string, payload: AnalyticsEventPayload): void => {
	if(typeof window === 'undefined' || !window.gtag) { return; }
	window.gtag('event', eventName, payload);
};
